import React, { useState } from "react";
import type { FactorDef, ScoreFactor } from "../../types/ambassadors";
import NumberField from "./NumberField";
import { count } from "./format";
import { FACTOR_HINTS, validFactorDefs, weightTotal } from "./score";

// The calculator's "Strategic Influence Score" card: rate the DJ 0–10 on each
// factor (with a private note on why), and see the overall score and the
// multiplier it gives. The multiplier can be typed by hand; clearing it goes
// back to the score's. "Settings" turns the factor names and weights into
// inputs, in place: those are shared by every new offer (and applied to this
// one); saved offers keep the ones they were scored with. Internal only —
// nothing here reaches Present mode.

const RATINGS = Array.from({ length: 11 }, (_, k) => k);

interface Props {
  factors: ScoreFactor[];
  score: number | null;
  /** The multiplier in use (typed by hand, else the score's). */
  multiplier: number | null;
  /** The multiplier typed by hand; null = the score's. */
  manual: number | null;
  onRate: (id: string, rating: number) => void;
  onNote: (id: string, note: string) => void;
  onManual: (value: number | null) => void;
  /** The shared factor names and weights. */
  sharedDefs: FactorDef[];
  /** Saves new shared names and weights; rejects if the server refused them. */
  onSaveDefs: (defs: FactorDef[]) => Promise<void>;
}

export default function StrategicScoreCard({ factors, score, multiplier, manual, onRate, onNote, onManual, sharedDefs, onSaveDefs }: Props) {
  const [editing, setEditing] = useState<FactorDef[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const total = editing ? weightTotal(editing) : 100;
  const valid = editing ? validFactorDefs(editing) : true;
  const edit = (k: number, patch: Partial<FactorDef>) => setEditing((list) => list && list.map((d, j) => (j === k ? { ...d, ...patch } : d)));

  const save = async () => {
    if (!editing || !valid) return;
    setSaving(true);
    setError("");
    try {
      await onSaveDefs(editing.map((d) => ({ ...d, name: d.name.trim() })));
      setEditing(null);
    } catch {
      setError("Couldn’t save the settings. Check your connection and try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="adm-card amb-card">
      <div className="amb-card-head">
        <h3 className="adm-section-title">Strategic Influence Score</h3>
        {!editing && (
          <button type="button" className="adm-btn-light amb-settings-btn" onClick={() => setEditing(sharedDefs.map((d) => ({ ...d })))}>
            Settings
          </button>
        )}
      </div>

      {editing ? (
        <div className="amb-score">
          <div className="amb-score-row amb-score-edit amb-score-head">
            <span>Factor name</span>
            <span>Weight</span>
          </div>
          {editing.map((d, k) => (
            <div className="amb-score-row amb-score-edit" key={d.id}>
              <input
                className={`amb-text${d.name.trim() ? "" : " amb-num-missing"}`}
                aria-label={`Factor ${k + 1} name`}
                maxLength={60}
                value={d.name}
                onChange={(e) => edit(k, { name: e.target.value })}
              />
              <NumberField
                label={`Factor ${k + 1} weight`}
                suffix="%"
                required
                value={Number.isFinite(d.weight) ? d.weight : null}
                onChange={(v) => edit(k, { weight: v ?? NaN })}
              />
            </div>
          ))}
          <p className={`amb-score-total${valid ? "" : " amb-score-total-bad"}`}>
            Total {Number.isFinite(total) ? `${count(total)}%` : "—"}
            {Number.isFinite(total) && Math.abs(total - 100) >= 0.001 && " — the weights must add up to 100%"}
          </p>
          <p className="amb-note">Shared by every new offer, and applied to this one. Saved offers keep the names and weights they were scored with.</p>
          {error && <p className="error-text">{error}</p>}
          <div className="amb-sched-actions">
            <button type="button" className="adm-btn-green" disabled={!valid || saving} onClick={save}>
              {saving ? "Saving…" : "Save settings"}
            </button>
            <button type="button" className="adm-btn-light" disabled={saving} onClick={() => setEditing(null)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="amb-score">
          <div className="amb-score-row amb-score-head">
            <span>Factor</span>
            <span>Weight</span>
            <span>Rating</span>
          </div>
          {factors.map((f) => (
            <div className="amb-score-factor" key={f.id}>
              <div className="amb-score-row">
                <span className="amb-score-name">{f.name}</span>
                <span className="amb-score-weight">{count(f.weight)}%</span>
                <span className="amb-score-rating">
                  <select className="amb-select" aria-label={`${f.name} rating`} value={f.rating} onChange={(e) => onRate(f.id, Number(e.target.value))}>
                    {(RATINGS.includes(f.rating) ? RATINGS : [...RATINGS, f.rating].sort((a, b) => a - b)).map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                  <span className="amb-score-of">/10</span>
                </span>
              </div>
              <input
                className="amb-score-note"
                aria-label={`${f.name} note`}
                maxLength={1000}
                placeholder={FACTOR_HINTS[f.id] ?? "Why this rating?"}
                value={f.note}
                onChange={(e) => onNote(f.id, e.target.value)}
              />
            </div>
          ))}
        </div>
      )}

      <div className="amb-score-sum">
        <div>
          <span className="amb-field-label">Overall score</span>
          <b>{score === null ? "—" : `${score.toFixed(2)}/10`}</b>
        </div>
        <div>
          <span className="amb-field-label">Influence multiplier</span>
          <span className="amb-sched-share">
            <NumberField
              label="Influence multiplier"
              suffix="×"
              decimals={[2, 2]}
              calculated={manual === null}
              value={multiplier}
              onChange={onManual}
            />
            {manual !== null && <span className="amb-tag">manual</span>}
          </span>
        </div>
      </div>
      <p className="amb-note">
        5/10 is neutral (1.00×); each point adds or takes 0.20×, from 0.5× to 2.0×. Type a multiplier to set it by hand; clear it to go
        back to the score’s. Ratings and notes are private.
      </p>
    </section>
  );
}
